import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog, DialogContent, DialogActions,
  Button, Typography, TextField, MenuItem, Select,
  InputLabel, FormControl, Stack, Box, Tooltip, IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import axios from 'axios';
import config from '../config/config';
import SnackbarAlert from './SnackbarAlert';
import SimpleSuccessSpinner from './LoadingSpinner';
import DoubleConfirmModal from './DoubleConfirmModal';

const ACCENT = '#0655A5';

const elegantInputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    backgroundColor: '#f8fafc',
    fontSize: 'clamp(0.72rem, 0.68rem + 0.25vw, 0.90rem)',
    height: 'clamp(30px, 2.5vw, 42px)',
    transition: 'box-shadow .15s, border-color .15s',
    '& fieldset': { borderColor: '#e2e8f0' },
    '&:hover fieldset': { borderColor: '#cbd5e1' },
    '&.Mui-focused fieldset': { borderColor: ACCENT },
    '&.Mui-error fieldset': { borderColor: '#ef4444' }
  },
  '& .MuiInputLabel-root': {
    fontSize: 'clamp(0.72rem, 0.68rem + 0.25vw, 0.90rem)',
    color: '#64748b',
    transform: 'translate(14px, clamp(5px, 0.6vw, 10px)) scale(1)',
    '&.MuiInputLabel-shrink': {
      transform: 'translate(14px, -8px) scale(0.75)',
      backgroundColor: '#fff',
      px: '4px'
    }
  },
  '& .MuiSelect-select': {
    display: 'flex',
    alignItems: 'center',
    py: 0,
    height: 'clamp(30px, 2.5vw, 42px) !important'
  }
};

const modalBtnSx = {
  textTransform: 'none',
  fontWeight: 600,
  fontSize: 'clamp(0.72rem, 0.68rem + 0.25vw, 0.90rem)',
  height: 'clamp(30px, 2.5vw, 42px)',
  px: 'clamp(10px, 1.2vw, 18px)',
  borderRadius: '8px'
};

const ModalCentroCosto = ({
  open,
  onClose,
  onSave,
  centroCostoId,
  formData,
  handleInputChange,
  usuario,
  empresaId
}) => {
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [confirmSaveOpen, setConfirmSaveOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('error');
  const [empresas, setEmpresas] = useState([]);
  const [proyectosList, setProyectosList] = useState([]);
  const [divisionesList, setDivisionesList] = useState([]);

  // Estados para creación rápida en línea de Proyecto y División
  const [modalNuevoProy, setModalNuevoProy] = useState(false);
  const [nombreNuevoProy, setNombreNuevoProy] = useState('');
  const [guardandoProy, setGuardandoProy] = useState(false);

  const [modalNuevaDiv, setModalNuevaDiv] = useState(false);
  const [nombreNuevaDiv, setNombreNuevaDiv] = useState('');
  const [proyectoSelectedForNuevaDiv, setProyectoSelectedForNuevaDiv] = useState('');
  const [guardandoDiv, setGuardandoDiv] = useState(false);

  const cargarCatalogos = useCallback(async () => {
    try {
      const res = await axios.post(`${config.apiBaseUrl}/catalogosNivelesCC`, { idEmpresa: null });
      setEmpresas(res.data?.empresas || []);
      setProyectosList(res.data?.proyectos || []);
      setDivisionesList(res.data?.divisiones || []);
    } catch (e) {
      console.error('Error cargando catálogos de niveles:', e);
    }
  }, []);

  useEffect(() => {
    if (open) {
      cargarCatalogos();
      setErrors({});
    }
  }, [open, cargarCatalogos]);

  // Filtrar proyectos disponibles según Empresa seleccionada estrictamente (solo los que tienen empresa asociada válida)
  const proyectosFiltrados = useMemo(() => {
    if (!formData.IDEMPRESA) {
      return proyectosList.filter(p => p.IDEMPRESA && Number(p.IDEMPRESA) > 0);
    }
    const empIdNum = Number(formData.IDEMPRESA);
    return proyectosList.filter(p => p.IDEMPRESA && Number(p.IDEMPRESA) === empIdNum);
  }, [proyectosList, formData.IDEMPRESA]);

  // Filtrar grupos/divisiones según Proyecto seleccionado y Empresa seleccionada estrictamente
  const divisionesFiltradas = useMemo(() => {
    let list = divisionesList;

    if (formData.IDEMPRESA) {
      const empIdNum = Number(formData.IDEMPRESA);
      list = list.filter(d => {
        const divEmp = d.IDEMPRESA ? Number(d.IDEMPRESA) : null;
        if (divEmp && divEmp === empIdNum) return true;
        // Si no tiene empresa propia pero su proyecto sí tiene la empresa
        if (d.ID_PROYECTO) {
          const proy = proyectosList.find(p => p.ID_PROYECTO === d.ID_PROYECTO);
          if (proy && proy.IDEMPRESA && Number(proy.IDEMPRESA) === empIdNum) return true;
        }
        return false;
      });
    } else {
      list = list.filter(d => (d.IDEMPRESA && Number(d.IDEMPRESA) > 0) || (d.ID_PROYECTO && proyectosList.some(p => p.ID_PROYECTO === d.ID_PROYECTO && p.IDEMPRESA)));
    }

    if (formData.ID_PROYECTO) {
      const pIdNum = Number(formData.ID_PROYECTO);
      list = list.filter(d => d.ID_PROYECTO && Number(d.ID_PROYECTO) === pIdNum);
    } else if (formData.PROYECTO) {
      const proyObj = proyectosList.find(p => p.NOMBRE_PROYECTO === formData.PROYECTO);
      if (proyObj) {
        list = list.filter(d => d.ID_PROYECTO && Number(d.ID_PROYECTO) === Number(proyObj.ID_PROYECTO));
      }
    }

    return list;
  }, [divisionesList, proyectosList, formData.ID_PROYECTO, formData.PROYECTO, formData.IDEMPRESA]);

  const handleCrearProyectoRapido = async () => {
    if (!nombreNuevoProy.trim()) return;
    setGuardandoProy(true);
    try {
      await axios.post(`${config.apiBaseUrl}/crearEditarProyecto`, {
        NOMBRE_PROYECTO: nombreNuevoProy.trim(),
        IDEMPRESA: formData.IDEMPRESA || null,
        SESSION: usuario
      });
      const nuevoNombre = nombreNuevoProy.trim();
      setNombreNuevoProy('');
      setModalNuevoProy(false);
      await cargarCatalogos();
      handleInputChange('PROYECTO', nuevoNombre);
    } catch (e) {
      console.error('Error creando proyecto rápido:', e);
    } finally {
      setGuardandoProy(false);
    }
  };

  const handleCrearDivisionRapida = async () => {
    if (!nombreNuevaDiv.trim()) return;
    setGuardandoDiv(true);
    try {
      let idProy = formData.ID_PROYECTO ? Number(formData.ID_PROYECTO) : null;
      if (!idProy && proyectoSelectedForNuevaDiv) {
        const pObj = proyectosList.find(p => p.NOMBRE_PROYECTO === proyectoSelectedForNuevaDiv || p.ID_PROYECTO === Number(proyectoSelectedForNuevaDiv));
        if (pObj) idProy = pObj.ID_PROYECTO;
      }

      await axios.post(`${config.apiBaseUrl}/crearEditarDivision`, {
        NOMBRE_DIVISION: nombreNuevaDiv.trim(),
        ID_PROYECTO: idProy,
        IDEMPRESA: formData.IDEMPRESA || null,
        SESSION: usuario
      });
      const nuevoNombreDiv = nombreNuevaDiv.trim();
      setNombreNuevaDiv('');
      setProyectoSelectedForNuevaDiv('');
      setModalNuevaDiv(false);
      await cargarCatalogos();
      handleInputChange('DIVISION', nuevoNombreDiv);
      handleInputChange('GRUPO_CC', nuevoNombreDiv);
    } catch (e) {
      console.error('Error creando división rápida:', e);
    } finally {
      setGuardandoDiv(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.CENTRO_COSTO?.trim()) newErrors.CENTRO_COSTO = 'El nombre del centro de costo es obligatorio.';
    if (!formData.NUMERO_CC?.toString().trim()) newErrors.NUMERO_CC = 'El número del centro de costo es obligatorio.';
    if (!formData.IDEMPRESA) newErrors.IDEMPRESA = 'Debe seleccionar una empresa.';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleGuardar = () => {
    if (!validateForm()) return;
    setConfirmSaveOpen(true);
  };

  const handleConfirmGuardar = async () => {
    setLoading(true);
    try {
      let idProy = formData.ID_PROYECTO ? Number(formData.ID_PROYECTO) : null;
      let idDiv = formData.ID_DIVISION ? Number(formData.ID_DIVISION) : null;
      let nomProy = formData.PROYECTO || null;
      let nomDiv = formData.DIVISION || formData.GRUPO_CC || null;

      if (!idProy && nomProy) {
        const pObj = proyectosList.find(p => p.NOMBRE_PROYECTO === nomProy);
        if (pObj) idProy = pObj.ID_PROYECTO;
      }
      if (!idDiv && nomDiv) {
        const dObj = divisionesList.find(d => d.NOMBRE_DIVISION === nomDiv);
        if (dObj) idDiv = dObj.ID_DIVISION;
      }

      const payload = {
        CENTRO_COSTO: formData.CENTRO_COSTO.trim(),
        NUMERO_CC: formData.NUMERO_CC.toString().trim(),
        ID_PROYECTO: idProy,
        ID_DIVISION: idDiv,
        PROYECTO: nomProy,
        DIVISION: nomDiv,
        GRUPO_CC: nomDiv,
        IDEMPRESA: Number(formData.IDEMPRESA),
        SESSION: usuario
      };

      if (centroCostoId) {
        payload.ID_UNICO = centroCostoId;
        payload.CCID = centroCostoId;
        await axios.post(`${config.apiBaseUrl}/crearEditarCentroCosto`, payload);
      } else {
        await axios.post(`${config.apiBaseUrl}/crearEditarCentroCosto`, payload);
      }

      setSnackbarSeverity('success');
      setSnackbarMessage('Centro de Costo guardado exitosamente.');
      setSnackbarOpen(true);
      onSave();
      onClose();
    } catch (e) {
      console.error('Error guardando centro de costo:', e);
      setSnackbarSeverity('error');
      setSnackbarMessage(e.response?.data?.error || 'Error al guardar centro de costo.');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        disableRestoreFocus
        onClose={onClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: '14px',
            overflow: 'hidden',
            boxShadow: '0 20px 48px rgba(0,0,0,0.18)',
            border: '1px solid #e2e8f0',
            maxWidth: 'clamp(340px, 35vw, 520px)'
          }
        }}
      >
        {/* Header Compacto con Gradiente Oficial */}
        <Box
          sx={{
            px: 'clamp(14px, 1.6vw, 22px)',
            py: 'clamp(10px, 1.2vw, 15px)',
            background: 'linear-gradient(135deg, #0655A5 0%, #0861B8 100%)',
            color: '#ffffff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.2
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 900,
                fontSize: 'clamp(0.88rem, 0.82rem + 0.35vw, 1.12rem)',
                lineHeight: 1.2,
                letterSpacing: '-0.01em'
              }}
              noWrap
            >
              {centroCostoId ? 'Editar Centro de Costo' : 'Nuevo Centro de Costo'}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                opacity: 0.88,
                fontSize: 'clamp(0.64rem, 0.60rem + 0.15vw, 0.78rem)',
                mt: 0.2,
                display: 'block'
              }}
            >
              Estructura de centros de costo
            </Typography>
          </Box>

          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: 'white',
              p: 'clamp(3px, 0.4vw, 6px)',
              bgcolor: 'rgba(255,255,255,0.12)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' }
            }}
          >
            <CloseIcon sx={{ fontSize: 'clamp(15px, 1.2vw, 20px)' }} />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 'clamp(14px, 1.6vw, 22px)', bgcolor: '#ffffff' }}>
          <Stack spacing={'clamp(10px, 1.2vw, 16px)'}>
            <TextField
              size="small"
              label="Nombre Centro de Costo *"
              value={formData.CENTRO_COSTO || ''}
              onChange={(e) => {
                handleInputChange('CENTRO_COSTO', e.target.value);
                if (errors.CENTRO_COSTO) setErrors(prev => ({ ...prev, CENTRO_COSTO: '' }));
              }}
              fullWidth
              error={Boolean(errors.CENTRO_COSTO)}
              helperText={errors.CENTRO_COSTO}
              sx={elegantInputSx}
            />

            <TextField
              size="small"
              label="Número CC *"
              value={formData.NUMERO_CC || ''}
              onChange={(e) => {
                handleInputChange('NUMERO_CC', e.target.value);
                if (errors.NUMERO_CC) setErrors(prev => ({ ...prev, NUMERO_CC: '' }));
              }}
              fullWidth
              error={Boolean(errors.NUMERO_CC)}
              helperText={errors.NUMERO_CC}
              sx={elegantInputSx}
            />

            {/* 1. EMPRESA */}
            <FormControl
              fullWidth
              size="small"
              error={Boolean(errors.IDEMPRESA)}
              sx={elegantInputSx}
            >
              <InputLabel>Empresa *</InputLabel>
              <Select
                value={formData.IDEMPRESA !== undefined && formData.IDEMPRESA !== null && formData.IDEMPRESA !== '' ? Number(formData.IDEMPRESA) : ''}
                label="Empresa *"
                onChange={(e) => {
                  const empId = e.target.value;
                  handleInputChange('IDEMPRESA', empId ? Number(empId) : '');
                  if (errors.IDEMPRESA) setErrors(prev => ({ ...prev, IDEMPRESA: '' }));
                }}
                MenuProps={{ disableRestoreFocus: true }}
              >
                {empresas.map((emp) => (
                  <MenuItem key={emp.EMPRESAID} value={Number(emp.EMPRESAID)}>
                    {emp.NOMBRE_EMPRESA}
                  </MenuItem>
                ))}
              </Select>
              {errors.IDEMPRESA && (
                <Typography variant="caption" sx={{ color: '#d32f2f', mt: 0.3, fontWeight: 600, fontSize: 'clamp(0.64rem, 0.60rem + 0.1vw, 0.72rem)' }}>
                  {errors.IDEMPRESA}
                </Typography>
              )}
            </FormControl>

            {/* 2. PROYECTO */}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <FormControl
                fullWidth
                sx={elegantInputSx}
                size="small"
              >
                <InputLabel>Proyecto</InputLabel>
                <Select
                  value={
                    formData.ID_PROYECTO 
                      ? Number(formData.ID_PROYECTO) 
                      : (proyectosList.find(p => p.NOMBRE_PROYECTO === formData.PROYECTO)?.ID_PROYECTO) || ''
                  }
                  label="Proyecto"
                  onChange={(e) => {
                    const selId = e.target.value;
                    const pObj = proyectosList.find(p => p.ID_PROYECTO === Number(selId));
                    handleInputChange('ID_PROYECTO', selId ? Number(selId) : null);
                    handleInputChange('PROYECTO', pObj ? pObj.NOMBRE_PROYECTO : '');
                    if (pObj && pObj.IDEMPRESA && !formData.IDEMPRESA) {
                      handleInputChange('IDEMPRESA', Number(pObj.IDEMPRESA));
                    }
                  }}
                  MenuProps={{ disableRestoreFocus: true }}
                >
                  <MenuItem value=""><em>-- Sin Proyecto --</em></MenuItem>
                  {proyectosFiltrados.map((proy) => (
                    <MenuItem key={proy.ID_PROYECTO} value={Number(proy.ID_PROYECTO)}>
                      {proy.NOMBRE_PROYECTO}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Tooltip title="Crear nuevo Proyecto rápido">
                <IconButton
                  onClick={() => {
                    setNombreNuevoProy('');
                    setModalNuevoProy(true);
                  }}
                  size="small"
                  sx={{
                    bgcolor: 'rgba(6, 85, 165, 0.08)',
                    color: ACCENT,
                    border: '1px solid rgba(6, 85, 165, 0.25)',
                    borderRadius: '8px',
                    height: 'clamp(30px, 2.5vw, 42px)',
                    width: 'clamp(30px, 2.5vw, 42px)',
                    p: 0,
                    '&:hover': { bgcolor: 'rgba(6, 85, 165, 0.16)' }
                  }}
                >
                  <AddIcon sx={{ fontSize: 'clamp(15px, 1.2vw, 20px)' }} />
                </IconButton>
              </Tooltip>
            </Box>

            {/* 3. GRUPO / DIVISIÓN */}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <FormControl
                fullWidth
                sx={elegantInputSx}
                size="small"
              >
                <InputLabel>Grupo</InputLabel>
                <Select
                  value={
                    formData.ID_DIVISION 
                      ? Number(formData.ID_DIVISION) 
                      : (divisionesList.find(d => d.NOMBRE_DIVISION === (formData.DIVISION || formData.GRUPO_CC))?.ID_DIVISION) || ''
                  }
                  label="Grupo"
                  onChange={(e) => {
                    const selId = e.target.value;
                    const divObj = divisionesList.find(d => d.ID_DIVISION === Number(selId));
                    handleInputChange('ID_DIVISION', selId ? Number(selId) : null);
                    handleInputChange('DIVISION', divObj ? divObj.NOMBRE_DIVISION : '');
                    handleInputChange('GRUPO_CC', divObj ? divObj.NOMBRE_DIVISION : '');

                    if (divObj) {
                      if (divObj.ID_PROYECTO) {
                        const proyObj = proyectosList.find(p => p.ID_PROYECTO === divObj.ID_PROYECTO);
                        if (proyObj) {
                          handleInputChange('ID_PROYECTO', Number(proyObj.ID_PROYECTO));
                          handleInputChange('PROYECTO', proyObj.NOMBRE_PROYECTO);
                        }
                      }
                      if (divObj.IDEMPRESA && !formData.IDEMPRESA) {
                        handleInputChange('IDEMPRESA', Number(divObj.IDEMPRESA));
                      }
                    }
                  }}
                  MenuProps={{ disableRestoreFocus: true }}
                >
                  <MenuItem value=""><em>-- Sin Grupo --</em></MenuItem>
                  {divisionesFiltradas.map((div) => (
                    <MenuItem key={div.ID_DIVISION} value={Number(div.ID_DIVISION)}>
                      {div.NOMBRE_DIVISION} {div.ID_PROYECTO ? `(${proyectosList.find(p => p.ID_PROYECTO === div.ID_PROYECTO)?.NOMBRE_PROYECTO || 'Proyecto'})` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Tooltip title="Crear nuevo Grupo rápido">
                <IconButton
                  onClick={() => {
                    setNombreNuevaDiv('');
                    setProyectoSelectedForNuevaDiv(formData.PROYECTO || '');
                    setModalNuevaDiv(true);
                  }}
                  size="small"
                  sx={{
                    bgcolor: 'rgba(6, 85, 165, 0.08)',
                    color: ACCENT,
                    border: '1px solid rgba(6, 85, 165, 0.25)',
                    borderRadius: '8px',
                    height: 'clamp(30px, 2.5vw, 42px)',
                    width: 'clamp(30px, 2.5vw, 42px)',
                    p: 0,
                    '&:hover': { bgcolor: 'rgba(6, 85, 165, 0.16)' }
                  }}
                >
                  <AddIcon sx={{ fontSize: 'clamp(15px, 1.2vw, 20px)' }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{
            px: 'clamp(14px, 1.6vw, 22px)',
            py: 'clamp(8px, 0.9vw, 12px)',
            bgcolor: '#ffffff',
            borderTop: '1px solid #f1f5f9',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1.2
          }}
        >
          <Button
            onClick={onClose}
            sx={{
              borderRadius: '8px',
              px: 'clamp(12px, 1.2vw, 18px)',
              py: 'clamp(4px, 0.4vw, 6px)',
              fontWeight: 700,
              fontSize: 'clamp(0.70rem, 0.66rem + 0.15vw, 0.82rem)',
              textTransform: 'none',
              color: '#64748b'
            }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleGuardar}
            sx={{
              borderRadius: '8px',
              px: 'clamp(14px, 1.5vw, 22px)',
              py: 'clamp(5px, 0.5vw, 7px)',
              fontWeight: 800,
              fontSize: 'clamp(0.70rem, 0.66rem + 0.15vw, 0.84rem)',
              textTransform: 'none',
              background: 'linear-gradient(135deg, #0655A5 0%, #0861B8 100%)',
              boxShadow: '0 3px 12px rgba(6, 85, 165, 0.25)',
              '&:hover': {
                background: 'linear-gradient(135deg, #054785 0%, #0655A5 100%)',
                boxShadow: '0 5px 16px rgba(6, 85, 165, 0.4)'
              }
            }}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <DoubleConfirmModal
        open={confirmSaveOpen}
        message="¿Deseas guardar los cambios?"
        handleConfirm={() => {
          setConfirmSaveOpen(false);
          handleConfirmGuardar();
        }}
        handleCancel={() => setConfirmSaveOpen(false)}
      />

      <SnackbarAlert
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />

      {loading && <SimpleSuccessSpinner />}

      {/* MODAL RÁPIDO NUEVO PROYECTO */}
      <Dialog
        open={modalNuevoProy}
        disableRestoreFocus
        onClose={() => setModalNuevoProy(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 24px 48px rgba(0,0,0,0.18)',
            border: '1px solid #e2e8f0'
          }
        }}
      >
        <Box
          sx={{
            px: 'clamp(14px, 1.8vw, 20px)',
            py: 'clamp(10px, 1.2vw, 14px)',
            background: 'linear-gradient(135deg, #0655A5 0%, #0861B8 100%)',
            color: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 800,
              fontSize: 'clamp(0.92rem, 0.88rem + 0.2vw, 1.05rem)'
            }}
          >
            Nuevo Proyecto
          </Typography>
          <IconButton
            size="small"
            onClick={() => setModalNuevoProy(false)}
            sx={{
              color: 'white',
              bgcolor: 'rgba(255,255,255,0.12)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' }
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <DialogContent sx={{ p: 'clamp(14px, 1.8vw, 20px)', bgcolor: '#ffffff' }}>
          <TextField
            autoFocus
            size="small"
            label="Nombre del Proyecto"
            fullWidth
            value={nombreNuevoProy}
            onChange={(e) => setNombreNuevoProy(e.target.value)}
            placeholder="Ej: PLATAFORMAS, INFRAESTRUCTURA..."
            sx={elegantInputSx}
          />
        </DialogContent>
        <DialogActions
          sx={{
            px: 'clamp(14px, 1.8vw, 20px)',
            py: 'clamp(8px, 1vw, 12px)',
            bgcolor: '#f8fafc',
            borderTop: '1px solid #e2e8f0',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1.2
          }}
        >
          <Button
            onClick={() => setModalNuevoProy(false)}
            disabled={guardandoProy}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              color: '#64748b',
              fontWeight: 700,
              fontSize: 'clamp(0.74rem, 0.72rem + 0.08vw, 0.82rem)'
            }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleCrearProyectoRapido}
            disabled={guardandoProy || !nombreNuevoProy.trim()}
            sx={{
              borderRadius: '8px',
              px: 'clamp(12px, 1.4vw, 18px)',
              py: 'clamp(4px, 0.5vw, 6px)',
              fontWeight: 800,
              fontSize: 'clamp(0.74rem, 0.72rem + 0.08vw, 0.82rem)',
              textTransform: 'none',
              background: 'linear-gradient(135deg, #0655A5 0%, #0861B8 100%)',
              boxShadow: '0 3px 10px rgba(6, 85, 165, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #054785 0%, #0655A5 100%)',
                boxShadow: '0 4px 14px rgba(6, 85, 165, 0.45)'
              }
            }}
          >
            {guardandoProy ? 'Guardando...' : 'Crear y Seleccionar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* MODAL RÁPIDO NUEVA DIVISIÓN */}
      <Dialog
        open={modalNuevaDiv}
        disableRestoreFocus
        onClose={() => setModalNuevaDiv(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 24px 48px rgba(0,0,0,0.18)',
            border: '1px solid #e2e8f0'
          }
        }}
      >
        <Box
          sx={{
            px: 'clamp(14px, 1.8vw, 20px)',
            py: 'clamp(10px, 1.2vw, 14px)',
            background: 'linear-gradient(135deg, #0655A5 0%, #0861B8 100%)',
            color: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 800,
              fontSize: 'clamp(0.92rem, 0.88rem + 0.2vw, 1.05rem)'
            }}
          >
            Nueva División / Grupo
          </Typography>
          <IconButton
            size="small"
            onClick={() => setModalNuevaDiv(false)}
            sx={{
              color: 'white',
              bgcolor: 'rgba(255,255,255,0.12)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' }
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <DialogContent sx={{ p: 'clamp(14px, 1.8vw, 20px)', bgcolor: '#ffffff' }}>
          <Stack spacing={2}>
            <TextField
              autoFocus
              size="small"
              label="Nombre del Grupo"
              fullWidth
              value={nombreNuevaDiv}
              onChange={(e) => setNombreNuevaDiv(e.target.value)}
              placeholder="Ej: DUCTOS, INDIRECTOS..."
              sx={elegantInputSx}
            />
            <FormControl fullWidth size="small" sx={elegantInputSx}>
              <InputLabel>Asociar a Proyecto (Opcional)</InputLabel>
              <Select
                value={proyectoSelectedForNuevaDiv}
                label="Asociar a Proyecto (Opcional)"
                onChange={(e) => setProyectoSelectedForNuevaDiv(e.target.value)}
                MenuProps={{ disableRestoreFocus: true }}
              >
                <MenuItem value=""><em>-- Sin Proyecto --</em></MenuItem>
                {proyectosList.map(p => (
                  <MenuItem key={p.ID_PROYECTO} value={p.NOMBRE_PROYECTO}>{p.NOMBRE_PROYECTO}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{
            px: 'clamp(14px, 1.8vw, 20px)',
            py: 'clamp(8px, 1vw, 12px)',
            bgcolor: '#f8fafc',
            borderTop: '1px solid #e2e8f0',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1.2
          }}
        >
          <Button
            onClick={() => setModalNuevaDiv(false)}
            disabled={guardandoDiv}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              color: '#64748b',
              fontWeight: 700,
              fontSize: 'clamp(0.74rem, 0.72rem + 0.08vw, 0.82rem)'
            }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleCrearDivisionRapida}
            disabled={guardandoDiv || !nombreNuevaDiv.trim()}
            sx={{
              borderRadius: '8px',
              px: 'clamp(12px, 1.4vw, 18px)',
              py: 'clamp(4px, 0.5vw, 6px)',
              fontWeight: 800,
              fontSize: 'clamp(0.74rem, 0.72rem + 0.08vw, 0.82rem)',
              textTransform: 'none',
              background: 'linear-gradient(135deg, #0655A5 0%, #0861B8 100%)',
              boxShadow: '0 3px 10px rgba(6, 85, 165, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #054785 0%, #0655A5 100%)',
                boxShadow: '0 4px 14px rgba(6, 85, 165, 0.45)'
              }
            }}
          >
            {guardandoDiv ? 'Guardando...' : 'Crear y Seleccionar'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ModalCentroCosto;